import { MobileSkeletonLoader } from '@/components/atoms/MobileSkeletonLoader/MobileSkeletonLoader'
import { getCardDetails } from '@/features/Cards/cards.helper'
import { createCardsQueryOptions } from '@/features/Cards/queries'
import { useModal } from '@/features/Modal/Modal.hooks'
import { parseError } from '@/shared/helpers/error'
import { useCardsStore } from '@/store/cards/cards'
import { useQuery } from '@tanstack/react-query'
import { Button, Flex, Modal } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './CardDetailsModal.module.scss'
import CloseIcon from '@/assets/CloseIcon.svg'
import { Card } from '@/features/Cards/cards.types'
import { CopyButton } from '@/components/molecules/CopyButton/CopyButton'

interface CardDetails {
  cardholderName?: string | null
  cardNumber?: string | null
  cvc?: string | null
}

interface CardState {
  cardDetails: CardDetails
  validThru?: string | null
}

function Skeleton () {
  return (
    <Flex justify="space-between" className="w-full" align="center">
      <Flex vertical gap={5}>
        <MobileSkeletonLoader height="15px" width="80px" />
        <MobileSkeletonLoader height="20px" width="150px" />
      </Flex>
      <div>
        <MobileSkeletonLoader height="30px" width="80px" />
      </div>
    </Flex>
  )
}

export function CardDetailsModal({ cardId }: { cardId: string }) {
  const [card, setCard] = useState<CardState | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const { destroyModal } = useModal()

  const { data: cards } = useQuery(createCardsQueryOptions())
  const selectedCardStore = useCardsStore((state) => state.selectedCard)

  const selectedCard: Card | undefined = cards?.find(
    (c) => c.id === selectedCardStore,
  )

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!cardId) {
      setIsLoading(false)
      setFetchError('No card ID provided.')
      return
    }

    let cancelled = false

    const init = async () => {
      setIsLoading(true)
      setFetchError(null)

      try {
        const req = await getCardDetails(cardId)

        if (cancelled) return

        const body = await req.json()

        if (cancelled) return

        if (!req.ok || body?.error) {
          throw new Error(body?.error ?? 'Failed to load card details')
        }

        if (isMounted.current) {
          setCard({ cardDetails: body ?? {} })
        }
      } catch (error) {
        if (cancelled) return
        console.error('[CardDetailsModal] Failed to load card details:', error)
        parseError(error)
        if (isMounted.current) {
          setFetchError('Failed to load card details. Please try again.')
        }
      } finally {
        if (!cancelled && isMounted.current) {
          setIsLoading(false)
        }
      }
    }

    init()

    return () => {
      cancelled = true
    }
  }, [cardId])

  const onClose = useCallback(() => {
    destroyModal()
  }, [destroyModal])

  const cardholderName = card?.cardDetails?.cardholderName ?? null
  const cardNumber = card?.cardDetails?.cardNumber ?? '—'
  const cvc = card?.cardDetails?.cvc ?? '—'
  const validThru = card?.validThru ?? selectedCard?.validThru ?? '—'

  return (
    <Modal
      rootClassName="cardDetailsModal"
      onCancel={onClose}
      open
      closeIcon={null}
      footer={null}
      title={<p className={styles.title}>Card details</p>}
      transitionName={null}
      maskTransitionName={null}
    >
      {isLoading && (
        <Flex vertical gap={15}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Flex>
      )}

      {!isLoading && fetchError && (
        <Flex vertical gap={12} align="center" style={{ padding: '24px 0' }}>
          <p style={{ color: 'var(--color-error, #ff4d4f)', margin: 0 }}>
            {fetchError}
          </p>
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </Flex>
      )}

      {!isLoading && !fetchError && card && (
        <div className={styles.list}>
          <span onClick={onClose} className={styles.close}>
            <CloseIcon />
          </span>
          <Flex gap={20} vertical>
            {cardholderName && (
              <Flex justify="space-between" align="center">
                <div>
                  <p className={styles.listTitle}>Cardholder:</p>
                  <p className={styles.listDescription}>{cardholderName}</p>
                </div>
                <CopyButton value={cardholderName} />
              </Flex>
            )}

            <Flex justify="space-between" align="center">
              <div>
                <p className={styles.listTitle}>Card number:</p>
                <p className={styles.listDescription}>{cardNumber}</p>
              </div>
              <CopyButton value={card.cardDetails.cardNumber} />
            </Flex>

            <Flex justify="space-between" align="center">
              <div>
                <p className={styles.listTitle}>Expiry date:</p>
                <p className={styles.listDescription}>{validThru}</p>
              </div>
              <CopyButton value={validThru !== '—' ? validThru : ''} />
            </Flex>

            <Flex justify="space-between" align="center">
              <div>
                <p className={styles.listTitle}>Security code:</p>
                <p className={styles.listDescription}>{cvc}</p>
              </div>
              <CopyButton value={card.cardDetails.cvc} />
            </Flex>
          </Flex>
        </div>
      )}
    </Modal>
  )
}
